import { useEffect, useState } from 'react';

import { fetchSimilar } from '../api';

/**
 * INTERNAL hook — not the public surface (that is `<RecommendedSection>`).
 *
 * Owns:
 *  - Fetch via api.js `fetchSimilar` (fail-soft to [])
 *  - `excludeIds` dedup: removes any result whose id is already in the visible
 *    keyword page results (TASK154 / TASK157 D5). Convergence point is the
 *    SearchPage, which passes the current page's listing ids.
 *  - Internal React state: loading / results / error (mirror `useEnrichments` —
 *    no Redux, no global state).
 *
 * The dead 0.35 client-side similarity filter is intentionally ABSENT. The
 * server already filters at the per-tenant threshold (default 0.60) — keeping
 * a lower client-side filter here would be both redundant and incorrect.
 *
 * ESLint note: no `exhaustive-deps` disable directive — see CONTRIBUTING.md.
 * The `dependencyKey` / `excludeKey` pattern is intentional and mirrors
 * `useEnrichments`.
 *
 * @param {string} keywords            - the search query
 * @param {{ excludeIds?: string[], limit?: number, rootUrl?: string }} [opts]
 * @returns {{ results: object[], loading: boolean, error: boolean }}
 */
export const useSemanticSearch = (keywords, { excludeIds, limit, rootUrl } = {}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Stable dependency keys so the effect only re-runs when the values change,
  // not on every render (mirrors `useEnrichments` dependencyKey approach).
  const keywordsTrimmed = keywords ? keywords.trim() : '';
  const excludeKey = Array.isArray(excludeIds) ? excludeIds.join(',') : '';

  useEffect(() => {
    if (!keywordsTrimmed) {
      setResults([]);
      setLoading(false);
      setError(false);
      return;
    }

    let isActive = true;
    setLoading(true);
    setError(false);

    fetchSimilar(keywordsTrimmed, { limit, rootUrl })
      .then(rawResults => {
        if (!isActive) return;

        // D5 / TASK154: exclude results whose id already appears on the
        // keyword-match page. `excludeIds` is the visible page's listing ids.
        const excludeSet = new Set(Array.isArray(excludeIds) ? excludeIds : []);
        const deduped = rawResults.filter(item => !excludeSet.has(item.listingId));

        setResults(deduped);
        setLoading(false);
      })
      .catch(() => {
        if (!isActive) return;
        setResults([]);
        setLoading(false);
        setError(true);
      });

    return () => {
      isActive = false;
    };
  }, [keywordsTrimmed, excludeKey, limit, rootUrl]);

  return { results, loading, error };
};
